import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Drawer } from 'antd';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { Theme } from '@routific/hive';
import { useStore } from '../../../store/mst';
import Panel, { PanelMode } from '../../shared/Panel';
import ConfirmAction from '../../shared/ConfirmAction';
import { JustifyContent } from '../../shared/Panel/PanelFooter';
import DriverSelector from '../../drivers/DriverSelector';
import AnalyticsEvents from '../../../analytics/analyticsEvents';
import { useTracking } from 'react-tracking';
import { DriverDtoModelType } from '../../../store/mst/models';
import { PANEL_WIDTH } from '../../../constants';

export const SELECTED_FILTERED_DIMENSION_LOCAL_STORAGE_KEY = 'selectedFilterDimensions';
export const SELECTED_FILTERED_DRIVER_IDS_LOCAL_STORAGE_KEY = 'selectedDrivers';

interface Props {
  drivers: DriverDtoModelType[];
}

const DriverSelectorContainer = styled.div`
  padding: ${Theme.Spacing.mdSpacing};
  gap: ${Theme.Spacing.mdSpacing};
  flex-grow: 1;
`;

const InsightsFilter: React.FC<Props> = ({ drivers }) => {
  const { t } = useTranslation('components');
  const { insights } = useStore();
  const { trackEvent } = useTracking();
  const { control, getValues, setValue, reset } = useForm();

  const onClose = () => {
    reset();
    insights.setShowFilterOptions(false);
  };

  useEffect(() => {
    return () => {
      onClose();
    };
  }, []);

  useEffect(() => {
    const previouslySelectedFilterDimensions = localStorage.getItem(SELECTED_FILTERED_DIMENSION_LOCAL_STORAGE_KEY);
    const previouslySelectedFilterIds = localStorage.getItem(SELECTED_FILTERED_DRIVER_IDS_LOCAL_STORAGE_KEY);

    if (previouslySelectedFilterDimensions && previouslySelectedFilterIds) {
      const selectedDriverIds = JSON.parse(previouslySelectedFilterIds) as string[];
      const selectedDrivers = drivers.filter((driver) => selectedDriverIds.includes(driver.id));
      insights.setSelectedDrivers(selectedDriverIds);
      setValue('driverSelector', selectedDrivers);
    }
  }, [drivers]);

  const onConfirm = () => {
    const selectedDrivers = getValues('driverSelector');
    if (selectedDrivers?.length) {
      insights.setSelectedDrivers(selectedDrivers.map((d: any) => d.id));
      trackEvent({
        event: AnalyticsEvents.INSIGHTS_DETAILS_FILTERED,
        properties: {
          metricName: insights.selectedMetric,
          filteredBy: [insights.selectedFilterDimensions],
        },
      });
    } else {
      insights.resetSelectedFilterDimensions();
    }

    insights.setShowFilterOptions(false);
  };

  return (
    <Drawer
      placement="right"
      visible={insights.showFilterOptions}
      destroyOnClose={true}
      contentWrapperStyle={{ width: PANEL_WIDTH, background: 'white', borderRadius: '16px 0 0 16px', overflow: 'hidden' }}
      bodyStyle={{ padding: 0, overflow: 'hidden' }}
      closable={false}
      onClose={onClose}
      data-cy='insights-details-filter-drawer'
      data-testid='insights-details-filter-drawer'
      mask
    >
      <Panel
        title={t('insights.filter.title')}
        mode={PanelMode.OPEN}
        onClose={onClose}
        footerAlignment={JustifyContent.FLEX_END}
        footerContent={
          <ConfirmAction
            cancel={{
              id: 'insights-details-filter-cancel-btn',
              onClick: onClose,
            }}
            confirm={{
              id: 'insights-details-filter-confirm-btn',
              label: t('insights.filter.footer.apply'),
              onClick: onConfirm,
            }}
            />
          }
      >
        <DriverSelectorContainer>
          <DriverSelector drivers={drivers} control={control} />
        </DriverSelectorContainer>
      </Panel>
    </Drawer>
  );
};

export default observer(InsightsFilter);
